import React, { useEffect, useState } from 'react';
import { LogoCrop } from '../../data/sister-organizations';
interface OrgLogoProps {
  src: string;
  alt: string;
  crop?: LogoCrop;
  /**
   * Fallback zoom, used only if the logo's empty margins can't be measured
   * automatically (e.g. the image host blocks pixel reads).
   */
  scale?: number;
  /** CSS filter for the image, e.g. to lift an off-white background to white. */
  filter?: string;
  /** Optical size multiplier for marks that still read small after balancing, e.g. 1.2 */
  boost?: number;
  /** Sizing/styling for the outer frame, e.g. 'w-56 h-28 rounded-xl bg-white'. */
  className?: string;
  lazy?: boolean;
}
type Trim = {
  region: [number, number, number, number];
  aspect: number;
};

/**
 * A square logo at this fraction of the frame height sets the visual size
 * every other logo is balanced against.
 */
const SQUARE_HEIGHT = 0.86;

/**
 * Renders an organization logo so every mark reads at a similar optical size.
 *
 * 1. Empty margins baked into the image are measured and trimmed away.
 * 2. The trimmed mark is sized by area rather than by its bounding box, so a
 *    square crest and a long wordmark carry similar visual weight: wide logos
 *    get shorter, tall/square logos get taller, all within the frame.
 */
export function OrgLogo({
  src,
  alt,
  crop,
  scale,
  filter,
  boost = 1,
  className = '',
  lazy
}: OrgLogoProps) {
  const loading = lazy ? 'lazy' : undefined;
  const trim = useLogoTrim(src, !crop);
  // Multiply blending lets any remaining near-white background disappear
  // into the white frame, leaving only the mark itself.
  const tone: React.CSSProperties = filter ? {
    filter,
    mixBlendMode: 'multiply'
  } : {};
  const fit = crop ?? trim;

  // Measurement unavailable: show the whole image, with the manual zoom.
  if (!fit) {
    return <div className={`flex items-center justify-center overflow-hidden ${className}`} data-id="element-3354" data-mp="31i9kxuyyad33">
        <img src={src} alt={alt} className="w-full h-full object-contain" style={{
        transform: (scale ?? 1) * boost !== 1 ? `scale(${(scale ?? 1) * boost})` : undefined,
        ...tone
      }} loading={loading} decoding="async" data-id="element-3355" data-mp="2xhhawzi9845r" />
      </div>;
  }
  const [left, top, width, height] = fit.region;
  const heightShare = Math.min(1, SQUARE_HEIGHT * boost / Math.sqrt(fit.aspect));
  const mask = crop?.mask;
  return <div className={`flex items-center justify-center ${className}`} style={{
    containerType: 'size'
  } as React.CSSProperties} data-id="element-3356" data-mp="1gff70i7hqa96">
      <div role="img" aria-label={alt} className="relative overflow-hidden" style={{
      aspectRatio: String(fit.aspect),
      // Area-balanced height, but never wider than the frame.
      height: `min(${heightShare * 100}cqh, ${100 / fit.aspect}cqw)`,
      clipPath: mask
    }} data-id="element-3357" data-mp="1kg8td75abgzy">
        <img src={src} alt="" aria-hidden="true" loading={loading} decoding="async" className="absolute max-w-none" style={{
        width: `${100 / width * 100}%`,
        height: `${100 / height * 100}%`,
        left: `${-left / width * 100}%`,
        top: `${-top / height * 100}%`,
        ...tone
      }} data-id="element-3358" data-mp="p86nngc8v8fr" />
      </div>
    </div>;
}

/**
 * Measures the visible mark inside a logo image by comparing every pixel to
 * the corner (background) color, returning the tight bounding box. Returns
 * null if the image can't be read.
 */
/**
 * Measurements are shared across every place a logo appears (homepage and
 * National Network page), so each image is analyzed once per visit.
 */
const trimCache = new Map<string, Promise<Trim | null>>();
function loadTrim(src: string): Promise<Trim | null> {
  const cached = trimCache.get(src);
  if (cached) return cached;
  const job = new Promise<Trim | null>(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      try {
        resolve(measureMark(img));
      } catch {
        // Pixel reads blocked — fall back to showing the full image.
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
  trimCache.set(src, job);
  return job;
}
type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: {
    timeout: number;
  }) => number;
  cancelIdleCallback?: (id: number) => void;
};
function useLogoTrim(src: string, enabled: boolean): Trim | null {
  const [trim, setTrim] = useState<Trim | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setTrim(null);
    const run = () => {
      loadTrim(src).then(result => {
        if (!cancelled && result) setTrim(result);
      });
    };
    // Analyze logos when the browser is idle so it never delays first paint.
    const w = window as IdleWindow;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(run, {
        timeout: 1500
      });
    } else {
      timeoutId = window.setTimeout(run, 200);
    }
    return () => {
      cancelled = true;
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [src, enabled]);
  return trim;
}
function measureMark(img: HTMLImageElement): Trim | null {
  const max = 200;
  const ratio = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * ratio));
  const h = Math.max(1, Math.round(img.naturalHeight * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', {
    willReadFrequently: true
  });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  const bgR = data[0];
  const bgG = data[1];
  const bgB = data[2];
  const bgTransparent = data[3] < 24;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const a = data[i + 3];
      if (a < 24) continue;
      if (!bgTransparent) {
        const d = Math.abs(data[i] - bgR) + Math.abs(data[i + 1] - bgG) + Math.abs(data[i + 2] - bgB);
        if (d < 48) continue;
      }
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX || maxY < minY) return null;

  // A pixel of breathing room so anti-aliased edges aren't clipped.
  minX = Math.max(0, minX - 1);
  minY = Math.max(0, minY - 1);
  maxX = Math.min(w - 1, maxX + 1);
  maxY = Math.min(h - 1, maxY + 1);
  const bw = maxX - minX + 1;
  const bh = maxY - minY + 1;
  return {
    region: [minX / w * 100, minY / h * 100, bw / w * 100, bh / h * 100],
    aspect: bw / bh
  };
}